import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import api from '../../../utils/api';
import { toast } from 'sonner';

const scannerDivId = 'catalog-barcode-scanner';

const BarcodeScanModal = ({ isOpen, onClose, onBarcodeScanned }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Idle');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [productData, setProductData] = useState(null);
  const scannerRef = useRef(null);
  const hasDecodedRef = useRef(false);
  const errorLogCountRef = useRef(0);

  useEffect(() => {
    console.log('[CatalogScanner] mounted');
    return () => {
      console.log('[CatalogScanner] unmount');
      if (scannerRef.current) {
        console.log('[CatalogScanner] Unmount -> stopping scanner');
        scannerRef.current.stop().catch(() => {});
        try { scannerRef.current.clear(); } catch {}
        scannerRef.current = null;
      }
    };
  }, []);

  const startScanner = async () => {
    console.log('[CatalogScanner] startScanner called');
    setStatus('Starting');
    setError('');
    setScanResult(null);
    hasDecodedRef.current = false;
    errorLogCountRef.current = 0;

    if (scannerRef.current) {
      console.log('[CatalogScanner] Scanner already running');
      setStatus('Running');
      return;
    }

    try {
      setIsScanning(true);
      let cameras = [];
      try {
        cameras = await Html5Qrcode.getCameras();
        console.log('[CatalogScanner] Cameras discovered:', cameras);
      } catch (camErr) {
        console.error('[CatalogScanner] getCameras failed:', camErr);
        setStatus('Camera discovery failed');
        setIsScanning(false);
        return;
      }
      if (!cameras || cameras.length === 0) {
        setError('No camera found. Try manual entry.');
        setStatus('No camera');
        setIsScanning(false);
        return;
      }
      const chosen = (cameras.find(c => /back|rear|environment/i.test(c.label)) || cameras[0]);
      const deviceId = chosen.id;
      console.log('[CatalogScanner] Using camera:', chosen);

      const container = document.getElementById(scannerDivId);
      if (!container) {
        console.warn('[CatalogScanner] Scanner container not found');
        setError('Scanner container not found');
        setStatus('Container missing');
        setIsScanning(false);
        return;
      }

      const scanner = new Html5Qrcode(scannerDivId, /* verbose= */ false);
      scannerRef.current = scanner;
      console.log('[CatalogScanner] Starting scanner...');
      await scanner.start(
        { deviceId: { exact: deviceId } },
        {
          fps: 15,
          qrbox: 260,
          aspectRatio: 1.7778,
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.CODE_93,
            Html5QrcodeSupportedFormats.CODE_128,
          ],
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        },
        (decodedText) => {
          if (hasDecodedRef.current) {
            console.log('[CatalogScanner] Already decoded, ignoring:', decodedText);
            return;
          }
          hasDecodedRef.current = true;
          console.log('[CatalogScanner] Successfully decoded barcode:', decodedText);
          setStatus('Decoded');
          setScanResult(decodedText);
          toast.success(`Barcode scanned: ${decodedText}`);
          stopScanner();
          // Automatically lookup the product after scanning
          console.log('[CatalogScanner] Starting automatic lookup...');
          lookupBarcode(decodedText);
        },
        (errMsg) => {
          if (errorLogCountRef.current < 10) {
            console.log('[CatalogScanner] decode attempt error:', errMsg);
            errorLogCountRef.current += 1;
          }
        }
      );
      setStatus('Running');
      console.log('[CatalogScanner] Scanner started successfully and is now scanning...');
      toast.info('Scanner ready! Point camera at barcode');
    } catch (e) {
      console.error('[CatalogScanner] Failed to start scanner:', e);
      setError(`Failed to start scanner: ${e.message}`);
      setStatus('Error');
      setIsScanning(false);
      toast.error(`Scanner error: ${e.message}`);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      console.log('[CatalogScanner] Stopping scanner');
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (e) {
        console.warn('[CatalogScanner] Error stopping/clearing scanner', e);
      }
      scannerRef.current = null;
    }
    setIsScanning(false);
    setStatus('Stopped');
  };

  const lookupBarcode = async (barcode) => {
    if (!barcode || !barcode.trim()) return;
    
    const code = barcode.trim();
    console.log('[CatalogScanner] lookupBarcode:', code);
    
    try {
      setIsLookingUp(true);
      setError('');
      
      // Try to find product in our inventory first
      const { data } = await api.get(`/barcode/${encodeURIComponent(code)}`);
      console.log('[CatalogScanner] Barcode lookup response:', data);
      
      if (data && data.id && data.source === 'firestore') {
        // Product found in our inventory
        setProductData(data);
        toast.success('Product found in inventory!');
        onBarcodeScanned(code, data);
        handleClose();
      } else if (data && data.source === 'openfoodfacts') {
        // Product found via external API
        setProductData(data);
        toast.info('Product found via Open Food Facts API');
        onBarcodeScanned(code, data);
        handleClose();
      } else {
        // No product found
        setError('Product not found. You can add it manually.');
        toast.warning('Product not found. You can add it manually.');
        onBarcodeScanned(code, null);
        handleClose();
      }
    } catch (err) {
      console.error('[CatalogScanner] Lookup error:', err);
      setError('Error looking up product. Try again or enter manually.');
      toast.error('Error looking up product');
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleBarcodeSubmit = (barcode) => {
    console.log('[CatalogScanner] handleBarcodeSubmit:', barcode);
    if (barcode && barcode.trim()) {
      lookupBarcode(barcode.trim());
    }
  };

  const handleManualSubmit = (e) => {
    e?.preventDefault();
    handleBarcodeSubmit(manualBarcode);
  };

  const handleClose = () => {
    console.log('[CatalogScanner] Closing modal');
    stopScanner();
    setManualBarcode('');
    setScanResult(null);
    setError('');
    onClose();
  };

  const handleUseScanResult = () => {
    if (scanResult) handleBarcodeSubmit(scanResult);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-md sm:mx-4 max-h-[95vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className="text-lg font-semibold text-foreground">Barcode Scanner</h3>
            <Button variant="ghost" size="icon" onClick={handleClose} disabled={isScanning}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          <div className="space-y-6">
            {/* Camera Scan Section */}
            <div className="text-center">
              <div className="w-full h-48 bg-muted border-2 border-dashed border-border rounded-lg flex items-center justify-center mb-4 overflow-hidden">
                {isScanning ? (
                  <div id={scannerDivId} className="w-full h-full" />
                ) : scanResult ? (
                  <div className="text-center">
                    <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
                    <p className="text-sm font-medium text-foreground">Barcode Detected!</p>
                    <p className="text-lg font-data text-primary mt-1">{scanResult}</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <Icon name="Camera" size={48} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Click "Start Camera Scan" to begin</p>
                    {error && <p className="text-xs text-destructive mt-2">{error}</p>}
                    <p className="text-[10px] text-muted-foreground mt-1">Status: {status}</p>
                  </div>
                )}
              </div>

              {scanResult ? (
                <div className="flex space-x-2">
                  <Button variant="default" onClick={handleUseScanResult} iconName="Check" iconPosition="left" iconSize={16} className="flex-1">
                    Use This Barcode
                  </Button>
                  <Button variant="outline" onClick={startScanner} iconName="RotateCcw" iconPosition="left" iconSize={16}>
                    Scan Again
                  </Button>
                </div>
              ) : (
                <Button
                  variant="default"
                  onClick={() => { console.log('[CatalogScanner] Start button clicked'); startScanner(); }}
                  disabled={isScanning}
                  loading={isScanning}
                  iconName="Camera"
                  iconPosition="left"
                  iconSize={16}
                  className="w-full"
                >
                  {isScanning ? 'Scanning...' : 'Start Camera Scan'}
                </Button>
              )}
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-card text-muted-foreground">or</span>
              </div>
            </div>

            {/* Manual Entry Section */}
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <Input
                label="Enter Barcode Manually"
                type="text"
                placeholder="Type or paste barcode number"
                value={manualBarcode}
                onChange={(e) => setManualBarcode(e?.target?.value)}
                error={error}
                description="Enter the barcode number if camera scanning is not available"
              />
              <div className="flex items-center space-x-3">
                <Button type="button" variant="ghost" onClick={handleClose} className="flex-1">Cancel</Button>
                <Button type="submit" variant="default" disabled={!manualBarcode?.trim()} iconName="Search" iconPosition="left" iconSize={16} className="flex-1">
                  Search Product
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanModal;